import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'app/store';

// ── Types ─────────────────────────────────────────────────────────────────────

/** One CloudWatch log entry as returned by the backend */
export interface CloudWatchLogEntry {
  '@timestamp': string;
  '@message': {
    level: string;
    message: string;
    function: string;
    timestamp: string;
    logId: string;
    method: string;
    path: string;
    brandId: string | null;
    locationId: string | null;
    requestTimestamp: string;
    status: number;
    responseTime: string;
    success: boolean;
    responseMessage: string;
    ip: string;
    userAgent: string | null;
    userId: string | null;
    username: string;
    module: string;
    action: string;
    mainBrandId: string | null;
    ref_id: string | null;
    requestQuery: Record<string, any>;
    requestBody: Record<string, any>;
  };
  '@ptr': string;
}

export interface ActivityLogState {
  loading: boolean;
  /** Available modules from GET /activityHistory/getModules */
  modules: { key: string; label: string; description: string }[];
  /** CloudWatch log entries */
  logs: CloudWatchLogEntry[];
  total: number;
  page: number;
  limit: number;
  module: string;
  moduleLabel: string;
  message: string;
  success: boolean;
}

export interface GetLogsPayload {
  module?: string;
  brandId?: string;
  locationId?: string;
  refId?: string;
  name?: string;
  action?: string;
  method?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  isQuery?: boolean;
  query?: string;
}

// ── Initial State ─────────────────────────────────────────────────────────────

const initialState: ActivityLogState = {
  loading: false,
  modules: [],
  logs: [],
  total: 0,
  page: 1,
  limit: 50,
  module: 'apiRequests',
  moduleLabel: '',
  message: '',
  success: false,
};

// ── Slice ─────────────────────────────────────────────────────────────────────

const activitylogSlice = createSlice({
  name: 'activitylog',
  initialState,
  reducers: {
    // Fetch module list
    getModules(state) {
      state.loading = true;
    },
    getModulesSuccess(state, action: PayloadAction<{ data: ActivityLogState['modules'] }>) {
      state.loading = false;
      state.modules = action.payload.data;
    },
    getModulesFailed(state, action: PayloadAction<{ message: string }>) {
      state.loading = false;
      state.message = action.payload.message;
    },

    // Fetch logs — payload forwarded to POST /activityHistory/getLogs
    getActivityLogs(state, _action: PayloadAction<GetLogsPayload>) {
      state.loading = true;
      state.message = '';
    },
    getActivityLogsSuccess(state, action: PayloadAction<any>) {
      const res = action.payload;
      state.loading = false;
      state.success = res.success;
      state.logs = res.data || [];
      state.total = res.total || 0;
      state.page = res.page || 1;
      state.limit = res.limit || 50;
      state.module = res.module || 'apiRequests';
      state.moduleLabel = res.moduleLabel || '';
    },
    getActivityLogsFailed(state, action: PayloadAction<any>) {
      state.loading = false;
      state.success = false;
      state.message = action.payload?.message || 'Failed to fetch logs';
    },

    clearData(state) {
      state.loading = false;
      state.message = '';
      state.success = false;
    },
  },
});

export const activitylogAction = activitylogSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectActivityLogs = (state: RootState) => state.activitylog.logs;
export const selectActivityLogTotal = (state: RootState) => state.activitylog.total;
export const selectActivityLogPage = (state: RootState) => state.activitylog.page;
export const selectActivityLogLimit = (state: RootState) => state.activitylog.limit;
export const selectActivityModules = (state: RootState) => state.activitylog.modules;
export const selectActivityModule = (state: RootState) => state.activitylog.module;
export const selectActivityModuleLabel = (state: RootState) => state.activitylog.moduleLabel;
export const selectIsLoading = (state: RootState) => state.activitylog.loading;
export const selectIsSuccess = (state: RootState) => state.activitylog.success;
export const selectMessage = (state: RootState) => state.activitylog.message;

// Legacy selector kept for backward compat (returns whole state.activitylog)
export const getActivityLogsData = (state: RootState) => state.activitylog;

const activitylogReducer = activitylogSlice.reducer;
export default activitylogReducer;
